import { z } from "zod";
// ---------------------------------------------------------------------------
// Connect
// ---------------------------------------------------------------------------
export const TallyConnectInputSchema = z.object({
    /** Full https Tally endpoint URL */
    tallyBaseUrl: z.string().url().startsWith("https://"),
    basicAuthUsername: z.string().optional(),
    basicAuthPassword: z.string().optional(),
    companyName: z.string().trim().min(1),
});
export const TallyListCompaniesInputSchema = z.object({
    /** Full https Tally endpoint URL */
    tallyBaseUrl: z.string().url().startsWith("https://"),
    basicAuthUsername: z.string().optional(),
    basicAuthPassword: z.string().optional(),
});
export const TallyListCompaniesOutputSchema = z.object({
    companies: z.array(z.string().min(1)),
});
// ---------------------------------------------------------------------------
// Status / response shapes
// ---------------------------------------------------------------------------
export const TallyResponseTypeSchema = z.enum([
    "endpoint_offline",
    "network_error",
    "timeout",
    "http_error",
    "payload_too_large",
    "malformed_response",
    "window_blocked",
    "company_not_selected",
    "active_company_mismatch",
]);
export const TallyStatusOutputSchema = z.object({
    connected: z.boolean(),
    status: z.enum(["connecting", "active", "paused", "error"]).nullable(),
    companyName: z.string().nullable(),
    lastSync: z.string().nullable(),
    lastSyncStatus: z
        .enum(["running", "pulling", "pulled", "transforming", "succeeded", "failed", "partial"])
        .nullable(),
    lastSyncResponseType: TallyResponseTypeSchema.nullable(),
    /**
     * "Agent data access" — true when a Neon DB has been provisioned for the
     * org AND at least one sync run has succeeded. The agent can read past
     * data whenever this is true, regardless of the live `status`.
     */
    dataAvailable: z.boolean(),
    /** ISO timestamp of the most recent successful sync, if any. */
    lastSyncedAt: z.string().nullable(),
    error: z.string().nullable(),
});
export const TallyAnalyticsOutputSchema = z.object({
    companyName: z.string().nullable(),
    latestVoucherDate: z.string().nullable(),
    totalVoucherCount: z.number().int().nonnegative(),
    countsByVoucherType: z.array(z.object({
        voucherType: z.string(),
        count: z.number().int().nonnegative(),
    })),
    recentRuns: z.array(z.object({
        id: z.string(),
        status: z.enum(["running", "pulling", "pulled", "transforming", "succeeded", "failed", "partial"]),
        responseType: TallyResponseTypeSchema.nullable(),
        startedAt: z.string(),
        finishedAt: z.string().nullable(),
        totalVouchers: z.number().int().nonnegative().nullable(),
    })),
});
// ---------------------------------------------------------------------------
// Manual sync (must match active Tally company)
// ---------------------------------------------------------------------------
export const TallySyncNowInputSchema = z.object({
    /** Optional override: active company name in Tally must contain this substring (case-insensitive). */
    expectedActiveCompanyContains: z.string().trim().min(1).optional(),
});
